import { useCallback, useEffect, useRef, type RefObject } from 'react';

/**
 * Decouples keyboard-driven highlight changes from mouse hover in a portaled
 * listbox (combobox / omnibox) where a single `highlightedIndex` is written by
 * both ArrowUp/ArrowDown and row `onMouseEnter`.
 *
 * It fixes two interaction bugs that share one root cause:
 *
 * 1. **Dropdown jump.** Scrolling the highlighted row into view with
 *    `element.scrollIntoView()` can scroll the window when the row sits near a
 *    viewport edge. Any capture-phase `window` scroll listener that repositions
 *    a fixed-position portal (the inline positioner in `Combobox`, or
 *    `useAnchoredPos`) then re-measures the anchor and visibly shifts the
 *    dropdown. This hook scrolls by mutating the list's own `scrollTop` instead,
 *    which never emits a `window` scroll event.
 *
 * 2. **Hover steals the keyboard highlight.** A stationary cursor inside the open
 *    list fires synthetic `mouseenter` events when the list scrolls under it
 *    during keyboard navigation, clobbering the keyboard-chosen row. The hook
 *    tracks the last input modality and gates hover via `shouldHoverHighlight`.
 *
 * The flag is a `useRef` (not state): it is read synchronously in event handlers
 * and the scroll effect, never rendered, so it must not trigger a re-render on
 * every arrow press.
 *
 * @param listRef - The portaled scroll container (`<ul role="listbox">`).
 * @param highlightedIndex - The active row index; `-1` when nothing is highlighted.
 * @param resolveItem - Locates the highlighted row element relative to the list.
 *   Defaults to `list.children[index]` for flat lists. Pass a resolver for
 *   grouped lists where the row is nested below the list's direct children.
 */
export function useKeyboardNavHighlight({
  listRef,
  highlightedIndex,
  resolveItem,
}: {
  listRef: RefObject<HTMLElement | null>;
  highlightedIndex: number;
  resolveItem?: (list: HTMLElement, index: number) => HTMLElement | null;
}): {
  /** Set keyboard-nav mode. Call in the ArrowUp / ArrowDown handlers. */
  markKeyboardNav: () => void;
  /** Clear keyboard-nav mode. Call on input change and on real pointer movement over the list. */
  markPointerNav: () => void;
  /** True when a row's `onMouseEnter` should move the highlight (i.e. not mid keyboard nav). */
  shouldHoverHighlight: () => boolean;
} {
  const keyboardNavRef = useRef(false);
  // Held in a ref so the scroll effect can keep `[highlightedIndex]` as its only
  // dependency: a consumer passing an inline `resolveItem` must not re-arm the effect.
  const resolveItemRef = useRef(resolveItem);
  resolveItemRef.current = resolveItem;

  // Scroll the highlighted row into view on keyboard navigation only, by adjusting
  // the list's scrollTop directly so the window never scrolls (see bug 1 above).
  useEffect(() => {
    if (!keyboardNavRef.current) return;
    if (highlightedIndex < 0 || !listRef.current) return;
    const list = listRef.current;
    const resolve = resolveItemRef.current;
    const item = resolve
      ? resolve(list, highlightedIndex)
      : (list.children[highlightedIndex] as HTMLElement | undefined) ?? null;
    if (!item) return;
    if (item.offsetTop < list.scrollTop) {
      list.scrollTop = item.offsetTop;
    } else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
  }, [highlightedIndex, listRef]);

  // Stable identities so consumers can list them in useCallback dependency
  // arrays without re-creating their own memoised handlers every render.
  const markKeyboardNav = useCallback(() => {
    keyboardNavRef.current = true;
  }, []);
  const markPointerNav = useCallback(() => {
    keyboardNavRef.current = false;
  }, []);
  const shouldHoverHighlight = useCallback(() => !keyboardNavRef.current, []);

  return { markKeyboardNav, markPointerNav, shouldHoverHighlight };
}
